import { FieldValues, useController } from 'react-hook-form';
import { Field, FieldProps as SWFieldProps } from '@spark-web/field';
import { TextInput, TextInputProps } from '@spark-web/text-input';
import type { FormFieldProps } from '../Form';

export type CustomTextInputFieldProps<TFieldValues extends FieldValues = FieldValues> = FormFieldProps<TFieldValues> &
  TextInputProps & { FieldProps?: Omit<SWFieldProps, 'label' | 'children'> } & {
    autoComplete?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    mask?: 'mm/yyyy';
  };

export function CustomTextInputField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  shouldUnregister,
  FieldProps,
  inputRef,
  mask,
  ...textInputProps
}: CustomTextInputFieldProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules: {
      onBlur: textInputProps?.onBlur,
      onChange: textInputProps?.onChange,
    },
    shouldUnregister,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (mask === 'mm/yyyy') {
      value = value.replace(/[^\d]/g, '');
      if (value.length > 2) {
        value = value.slice(0, 2) + '/' + value.slice(2, 6);
      }
      if (value.length > 7) value = value.slice(0, 7);
    }
    field.onChange(value);
  };

  return (
    <Field
      label={label}
      {...FieldProps}
      tone={error ? 'critical' : FieldProps?.tone}
      message={error?.message ?? FieldProps?.message}
    >
      <TextInput {...textInputProps} {...field} onChange={handleChange} ref={inputRef} />
    </Field>
  );
}

export default CustomTextInputField;
